import { useEffect, useState, useRef, useCallback } from 'react';
import api from '../api';
import { Gauge, RefreshCw, Pause, Play, ChevronDown, ChevronRight,
         Cpu, PhoneCall, Wallet, TrendingUp, AlertTriangle, Clock } from 'lucide-react';

const fmt  = (n) => Number(n || 0).toLocaleString('es-CO', { maximumFractionDigits: 0 });
const money = (n) => `$${fmt(n)}`;
const pctColor = (p) => p >= 40 ? 'var(--success)' : p >= 15 ? 'var(--warning)' : 'var(--danger)';

const ESTADO = {
  activa:           { txt: 'Llamando ahora', c: 'var(--success)', pulse: true },
  en_cola:          { txt: 'En cola',       c: 'var(--info)' },
  pausada_horario:  { txt: 'Pausada (horario)', c: 'var(--warning)' },
  programada:       { txt: 'Programada',    c: 'var(--info)' },
  finalizada:       { txt: 'Finalizada',    c: 'var(--text-3)' },
};
const estadoBadge = (e) => ESTADO[e] || { txt: e || '—', c: 'var(--text-3)' };

function Dot({ color, pulse }) {
  return (
    <span style={{ position: 'relative', width: 8, height: 8, borderRadius: '50%', background: color, display: 'inline-block',
      boxShadow: pulse ? `0 0 8px ${color}` : 'none' }}>
      {pulse && <span style={{ position: 'absolute', inset: -3, borderRadius: '50%', border: `1px solid ${color}`,
        animation: 'iclivepulse 1.6s ease-out infinite' }} />}
    </span>
  );
}

function Kpi({ icon: Icon, label, value, sub, color }) {
  return (
    <div style={{ background: 'var(--bg)', border: '1px solid var(--surface)', borderRadius: 12, padding: '14px 16px', minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 8, color: 'var(--text-3)' }}>
        {Icon && <Icon size={13} />}
        <span style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '.08em' }}>{label}</span>
      </div>
      <div style={{ fontSize: 20, fontWeight: 700, color: color || 'var(--text)', lineHeight: 1.1, fontVariantNumeric: 'tabular-nums' }}>{value}</div>
      {sub != null && <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 3 }}>{sub}</div>}
    </div>
  );
}

// Barra de progreso segmentada: completados vs en proceso vs pendientes
function Progreso({ c }) {
  // Barrido (lanzamiento) en curso: en un barrido de "No contesta" la barra de la campaña
  // completa ya marca 100% desde el inicio, así que se muestra el avance propio del barrido.
  const ba = c.barrido_actual;
  if (ba && (c.en_proceso > 0 || ba.pendientes > 0)) {
    return (
      <div>
        <div style={{ display: 'flex', height: 8, borderRadius: 6, overflow: 'hidden', background: 'var(--surface)', border: '1px solid var(--glass-brd)' }}>
          <div style={{ width: `${ba.pct}%`, background: 'var(--success)' }} title={`Marcados: ${ba.contactos_llamados}`} />
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 7, fontSize: 11, color: 'var(--text-2)', fontVariantNumeric: 'tabular-nums' }}>
          <span style={{ fontWeight: 700, color: 'var(--text)' }}>Barrido {ba.numero} · {ba.pct}%</span>
          <span>{fmt(ba.contactos_llamados)} / {fmt(ba.programados)}</span>
          {c.en_proceso > 0 && <span style={{ color: 'var(--warning)' }}>● {c.en_proceso} en curso</span>}
          <span style={{ color: 'var(--text-3)' }}>{fmt(ba.pendientes)} por marcar</span>
        </div>
      </div>
    );
  }
  const total = c.total || 0;
  const seg = (v, col) => total ? `${(v / total) * 100}%` : '0%';
  return (
    <div>
      <div style={{ display: 'flex', height: 8, borderRadius: 6, overflow: 'hidden', background: 'var(--surface)', border: '1px solid var(--glass-brd)' }}>
        <div style={{ width: seg(c.completados), background: 'var(--success)' }} title={`Completados: ${c.completados}`} />
        <div style={{ width: seg(c.en_proceso), background: 'var(--warning)' }} title={`En proceso: ${c.en_proceso}`} />
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 7, fontSize: 11, color: 'var(--text-2)', fontVariantNumeric: 'tabular-nums' }}>
        <span style={{ fontWeight: 700, color: 'var(--text)' }}>{c.pct}%</span>
        <span>{fmt(c.completados)} / {fmt(c.total)}</span>
        {c.en_proceso > 0 && <span style={{ color: 'var(--warning)' }}>● {c.en_proceso} en curso</span>}
        <span style={{ color: 'var(--text-3)' }}>{fmt(c.pendientes)} pendientes</span>
      </div>
    </div>
  );
}

// Chips de resultados
function Resultados({ c }) {
  const items = [
    { k: 'Contestadas', v: c.contestadas, col: 'var(--success)' },
    { k: 'Acuerdos/PTP', v: c.acuerdos, col: 'var(--accent)' },
    { k: 'Transferidos', v: c.transferidos, col: 'var(--teal)' },
    { k: 'Buzón', v: c.buzon, col: 'var(--info)' },
    { k: 'Sin respuesta', v: c.sin_respuesta, col: 'var(--warning)' },
    { k: 'No contestó', v: c.no_contesto, col: 'var(--text-3)' },
    { k: 'Rechazadas', v: c.rechazadas, col: 'var(--danger)' },
  ].filter(x => x.v > 0);
  if (!items.length) return null;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7, marginTop: 10 }}>
      {items.map((x, i) => (
        <span key={i} style={{ fontSize: 11, fontWeight: 600, color: x.col, background: 'var(--glass)',
          border: '1px solid var(--glass-brd)', borderRadius: 20, padding: '3px 10px', fontVariantNumeric: 'tabular-nums' }}>
          {x.k}: {fmt(x.v)}
        </span>
      ))}
    </div>
  );
}

function CampanaCard({ c, detalle }) {
  const b = estadoBadge(c.estado);
  const tasa = c.contestadas > 0 ? Math.round((c.acuerdos / c.contestadas) * 100) : 0;
  return (
    <div style={{ background: 'var(--bg-2)', border: '1px solid var(--surface)', borderRadius: 12, padding: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
          <Dot color={b.c} pulse={b.pulse} />
          <span style={{ fontWeight: 700, color: 'var(--text)', fontSize: 14 }}>{c.nombre || 'Campaña'}</span>
          <span style={{ fontSize: 11, fontWeight: 600, color: b.c }}>{b.txt}</span>
        </div>
        {(c.hora_inicio || c.hora_fin) && (
          <span style={{ fontSize: 11, color: 'var(--text-3)', display: 'flex', alignItems: 'center', gap: 5 }}>
            <Clock size={12} /> {c.hora_inicio || '—'}–{c.hora_fin || '—'}
          </span>
        )}
      </div>
      <Progreso c={c} />
      <Resultados c={c} />
      {tasa > 0 && (
        <div style={{ marginTop: 10, fontSize: 12, color: 'var(--text-2)' }}>
          Tasa de acuerdo: <b style={{ color: pctColor(tasa) }}>{tasa}%</b> de las contestadas
        </div>
      )}
      {detalle && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--surface)',
          fontSize: 12, color: 'var(--text-2)', fontVariantNumeric: 'tabular-nums' }}>
          {c.estado === 'finalizada' && c.duracion_min != null && (
            <span>⏱️ Duró <b style={{ color: 'var(--text)' }}>{c.duracion_min >= 60 ? `${(c.duracion_min / 60).toFixed(1)} h` : `${c.duracion_min} min`}</b></span>
          )}
          <span>🗣️ {fmt(c.minutos_hablados)} min hablados</span>
          <span>💵 Ingresos: <b style={{ color: 'var(--text)' }}>{money(c.ingresos_cop)}</b></span>
        </div>
      )}
    </div>
  );
}

export default function AdminLive() {
  const [data, setData]       = useState(null);
  const [err, setErr]         = useState(null);
  const [paused, setPaused]   = useState(false);
  const [openId, setOpenId]   = useState(null);
  const [detalle, setDetalle] = useState(null);
  const [detLoading, setDetLoading] = useState(false);
  const openRef = useRef(null);
  openRef.current = openId;

  const cargar = useCallback(async () => {
    try {
      const r = await api.get('/admin/live');
      setData(r.data); setErr(null);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'No se pudo cargar');
    }
  }, []);

  const cargarDetalle = useCallback(async (cid) => {
    try {
      const r = await api.get(`/admin/cliente/${cid}/campanas-vivo`);
      if (openRef.current === cid) setDetalle(r.data);
    } catch (e) { /* silencioso */ }
  }, []);

  useEffect(() => {
    cargar();
    const iv = setInterval(() => {
      if (paused) return;
      cargar();
      if (openRef.current) cargarDetalle(openRef.current);
    }, 6000);
    return () => clearInterval(iv);
  }, [cargar, cargarDetalle, paused]);

  const toggle = (cid) => {
    if (openId === cid) { setOpenId(null); setDetalle(null); return; }
    setOpenId(cid); setDetalle(null); setDetLoading(true);
    cargarDetalle(cid).finally(() => setDetLoading(false));
  };

  const pod = data?.pod || {};
  const conc = data?.concurrencia || {};
  const tot = data?.totales_hoy || {};
  const clientes = data?.clientes || [];
  const activos = clientes.filter(c => c.campanas.length > 0 || c.en_curso > 0).length;

  return (
    <div>
      <style>{`@keyframes iclivepulse{0%{transform:scale(1);opacity:.7}100%{transform:scale(2.4);opacity:0}}`}</style>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 6 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Gauge size={22} color="var(--accent)" />
          <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--text)', margin: 0 }}>Operaciones en vivo</h1>
          <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600, color: paused ? 'var(--text-3)' : 'var(--success)' }}>
            <Dot color={paused ? 'var(--text-3)' : 'var(--success)'} pulse={!paused} />
            {paused ? 'En pausa' : 'En directo'}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 11, color: 'var(--text-3)' }}>
            {data?.ts ? `Actualizado ${new Date(data.ts * 1000).toLocaleTimeString('es-CO')}` : ''}
          </span>
          <button onClick={() => setPaused(p => !p)} title={paused ? 'Reanudar' : 'Pausar'}
            style={{ background: 'var(--glass)', border: '1px solid var(--glass-brd)', color: 'var(--text-2)', borderRadius: 8, padding: '6px 10px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
            {paused ? <Play size={13} /> : <Pause size={13} />}{paused ? 'Reanudar' : 'Pausar'}
          </button>
          <button onClick={cargar} title="Actualizar ahora"
            style={{ background: 'var(--glass)', border: '1px solid var(--glass-brd)', color: 'var(--text-2)', borderRadius: 8, padding: '6px 10px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
            <RefreshCw size={13} />
          </button>
        </div>
      </div>
      <p style={{ color: 'var(--text-3)', marginBottom: 20, fontSize: 13 }}>
        Todo lo que está corriendo ahora mismo · {activos} cliente(s) activo(s) · se refresca cada 6s
      </p>

      {err && <div style={{ background: 'var(--danger-soft)', border: '1px solid var(--danger)', color: 'var(--danger)', borderRadius: 10, padding: 12, marginBottom: 16, fontSize: 13 }}>{err}</div>}

      {/* KPIs de HOY */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12, marginBottom: 16 }}>
        <Kpi icon={PhoneCall} label="Llamadas en conversación" value={conc.en_conversacion ?? conc.global ?? 0}
             sub={conc.en_conversacion !== undefined ? `${conc.marcando ?? 0} marcando · ${activos} cliente(s) activo(s)` : `${activos} cliente(s) activo(s)`}
             color={(conc.en_conversacion ?? conc.global) > 0 ? 'var(--success)' : 'var(--text)'} />
        <Kpi icon={TrendingUp} label="Ingresos hoy" value={money(tot.ingresos_cop)} sub={`${fmt(tot.llamadas)} llamadas`} />
        <Kpi icon={Wallet} label="Costo hoy" value={money(tot.costo_total_cop)} />
        <Kpi icon={TrendingUp} label="Margen hoy" value={money(tot.margen_cop)}
             sub={`${tot.margen_pct ?? 0}% margen`} color={pctColor(tot.margen_pct ?? 0)} />
        <Kpi icon={Cpu} label="Servidor de voz (GPU)"
             value={pod.on ? 'Encendido' : 'Apagado'}
             sub={pod.on ? `${pod.gpu || ''} · $${pod.cost_hr}/h · sesión $${(pod.session_cost_usd || 0).toFixed(2)}` : (pod.state || 'off')}
             color={pod.on ? 'var(--success)' : 'var(--text-3)'} />
      </div>

      {/* Clientes */}
      <h2 style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)', margin: '0 0 12px' }}>Clientes</h2>
      {!data && !err && <div style={{ color: 'var(--text-3)', fontSize: 13 }}>Cargando…</div>}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {clientes.map((c) => {
          const abierto = openId === c.id;
          const activo = c.campanas.length > 0 || c.en_curso > 0;
          return (
            <div key={c.id} style={{ background: 'var(--bg)', border: `1px solid ${activo ? 'var(--glass-brd)' : 'var(--surface)'}`, borderRadius: 12, overflow: 'hidden' }}>
              {/* Fila cliente (clickable) */}
              <div onClick={() => toggle(c.id)} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px', cursor: 'pointer', flexWrap: 'wrap' }}>
                <span style={{ color: 'var(--text-3)', display: 'flex' }}>{abierto ? <ChevronDown size={16} /> : <ChevronRight size={16} />}</span>
                {activo ? <Dot color="var(--success)" pulse /> : <span style={{ width: 8, height: 8 }} />}
                <span style={{ fontWeight: 700, color: 'var(--text)', fontSize: 15, minWidth: 140 }}>{c.nombre || 'Cliente'}</span>

                <div style={{ display: 'flex', alignItems: 'center', gap: 18, marginLeft: 'auto', flexWrap: 'wrap', fontVariantNumeric: 'tabular-nums' }}>
                  {c.en_curso > 0 && (
                    <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--success)' }}>{c.en_curso} en línea</span>
                  )}
                  {c.campanas.length > 0 && (
                    <span style={{ fontSize: 12, color: 'var(--text-2)' }}>{c.campanas.length} campaña(s) activa(s)</span>
                  )}
                  <span style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 5,
                    color: c.saldo_bajo ? 'var(--danger)' : 'var(--text-2)' }}>
                    {c.saldo_bajo && <AlertTriangle size={13} />}
                    Saldo {money(c.saldo_cop)}
                    <span style={{ color: 'var(--text-3)' }}>({fmt(c.min_restantes)} min)</span>
                  </span>
                  <span style={{ fontSize: 12, color: 'var(--text-3)' }}>
                    Hoy: <b style={{ color: (c.hoy.margen_cop || 0) >= 0 ? 'var(--success)' : 'var(--danger)' }}>{money(c.hoy.margen_cop)}</b> margen
                  </span>
                </div>
              </div>

              {/* Campañas activas inline (siempre visibles si hay) */}
              {c.campanas.length > 0 && !abierto && (
                <div style={{ padding: '0 16px 14px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {c.campanas.map((cp) => <CampanaCard key={cp.id} c={cp} />)}
                </div>
              )}

              {/* Drill-down completo */}
              {abierto && (
                <div style={{ padding: '0 16px 16px', borderTop: '1px solid var(--surface)' }}>
                  {detLoading && <div style={{ color: 'var(--text-3)', fontSize: 13, padding: '12px 0' }}>Cargando campañas…</div>}
                  {detalle && detalle.campanas.length === 0 && (
                    <div style={{ color: 'var(--text-3)', fontSize: 13, padding: '12px 0' }}>Este cliente no tiene campañas.</div>
                  )}
                  {detalle && detalle.campanas.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 12 }}>
                      {detalle.campanas.map((cp) => <CampanaCard key={cp.id} c={cp} detalle />)}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
